import {
  BaseInput,
  Button,
  ButtonVariant,
  DateInput,
  Show
} from "shared/components";
import { CombinedIncomeLayout } from "../components/Layout";
import { COMBINED_INCOME_FORM_ID } from "../constants";
import { toast } from "react-toastify";
import { CombinedIncomeSubStep } from "../../../constants";
import { useFormTyped } from "shared/hooks/use-form-typed";
import { object, string } from "yup";
import { DateTime } from "luxon";
import { IncomeType } from "shared/types";
import { useCombinedIncome } from "../context";
import { getErrorMessage } from "utils/error";

type CompanyDetailsFormData = {
  company_name: string;
  job_title: string;
  start_date: string;
};

const createValidationSchema = (
  isFieldRequired: (path: string[]) => boolean,
  incomeType?: IncomeType | null
) => {
  const isOfferLetter = incomeType === IncomeType.OfferLetter;
  const startDateRule = isOfferLetter
    ? string()
        .required("Start date is required")
        .test(
          "date-in-future",
          "Start date should not be earlier than the current day",
          (value) => {
            if (!value) return false;
            const date = DateTime.fromISO(value);
            if (!date.isValid) return false;
            return date.startOf("day") >= DateTime.now().startOf("day");
          }
        )
    : string()
        .required("Start date is required")
        .test("date-in-past", "Start date should be in the past", (value) => {
          if (!value) return false;
          const date = DateTime.fromISO(value);
          return date.isValid && date <= DateTime.now();
        });
  const jobTitleRule = isFieldRequired(["job_title"])
    ? string().required("Job title is required")
    : string();

  return object({
    company_name: string().required("Company name is required"),
    job_title: jobTitleRule,
    start_date: startDateRule
  });
};

export const CompanyDetailsStep = () => {
  const {
    updateStepMutation,
    getNextStep,
    stepData,
    refetchVerification,
    isFieldRequired,
    isFieldVisible,
    skipSupervisorDetails
  } = useCombinedIncome();

  const { register, onSubmit } = useFormTyped<CompanyDetailsFormData>(
    {
      company_name: stepData.company_name || "",
      job_title: stepData.job_title || "",
      start_date: stepData.start_date || ""
    },
    createValidationSchema(isFieldRequired, stepData.currentIncomeType)
  );

  const handleSubmit = async (formValues: CompanyDetailsFormData) => {
    const nextStep = getNextStep(CombinedIncomeSubStep.CompanyDetails, {
      incomeType: stepData.currentIncomeType,
      skipSupervisorDetails
    });
    if (!nextStep) return;

    updateStepMutation.mutate(
      {
        lastStep: nextStep,
        company_name: formValues.company_name,
        job_title: formValues.job_title,
        start_date: formValues.start_date
      },
      {
        onSuccess: () => {
          refetchVerification();
        },
        onError: (error) => {
          toast.error(
            getErrorMessage(error) || "Failed to save company details"
          );
        }
      }
    );
  };

  return (
    <form
      id={COMBINED_INCOME_FORM_ID}
      onSubmit={onSubmit({ onSuccess: handleSubmit })}
    >
      <CombinedIncomeLayout
        title="Add company details"
        footer={
          <Button
            className="w-[125px]"
            type="submit"
            form={COMBINED_INCOME_FORM_ID}
            loading={updateStepMutation.isPending}
            variant={ButtonVariant.PRIMARY}
          >
            Continue
          </Button>
        }
      >
        <BaseInput
          {...register("company_name")}
          id="company-name"
          label="Company name"
          placeholder="Enter company name"
          labelClassName="mt-4"
          required
        />

        <Show if={isFieldVisible(["job_title"])}>
          <BaseInput
            {...register("job_title")}
            id="job-title"
            label="Job title"
            placeholder="Nurse"
            labelClassName="mt-4"
            required={isFieldRequired(["job_title"])}
          />
        </Show>

        <div className="mt-4">
          <DateInput
            {...register("start_date")}
            id="start-date"
            label="Start date"
            ignoreDay={stepData.currentIncomeType !== IncomeType.OfferLetter}
            required
          />
        </div>
      </CombinedIncomeLayout>
    </form>
  );
};
